import { useEffect, useContext, isValidElement } from 'react';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import unset from 'lodash/unset';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { useListContext, FormGroupsProvider, FilterFormBase, getFilterFormValues, FilterContext } from 'react-admin';

const mergeInitialValuesWithDefaultValues = (initialValues, filters) => ({
    ...(filters || [])
        .filter(filterElement => isValidElement(filterElement) && filterElement.props.alwaysOn && filterElement.props.defaultValue)
        .reduce((acc, filterElement) => set({ ...acc }, filterElement.props.source, filterElement.props.defaultValue), {}),
    ...initialValues,
});

/**
 * Drop-in FilterForm replacement: merges form updates into existing filterValues
 * so filters outside react-hook-form (CustomSelectArrayInput, PeriodFilterInput, URL) are preserved.
 */
export const SyncedFilterForm = ({ defaultValues, filters: filtersProps, ...rest }) => {
    const { setFilters, displayedFilters, filterValues } = useListContext();
    const filters = useContext(FilterContext) || filtersProps;

    const mergedInitialValues = mergeInitialValuesWithDefaultValues(defaultValues || filterValues, filters);

    const form = useForm({
        defaultValues: mergedInitialValues,
    });
    const { getValues, reset, trigger, watch } = form;

    useEffect(() => {
        const newValues = getFilterFormValues(getValues(), filterValues);
        const previousValues = getValues();
        if (!isEqual(newValues, previousValues)) {
            reset(newValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- filterValues compared by value via stringify
    }, [JSON.stringify(filterValues), filterValues, getValues, reset]);

    useEffect(() => {
        const subscription = watch(async (values, { name }) => {
            const isFormValid = await trigger();

            if (!name || !isFormValid) {
                return;
            }

            if (get(values, name) === '') {
                const newValues = cloneDeep(values);
                unset(newValues, name);
                const nextFilters = cloneDeep(filterValues);
                unset(nextFilters, name);
                setFilters({ ...nextFilters, ...newValues }, displayedFilters, false);
                return;
            }

            setFilters({ ...filterValues, ...values }, displayedFilters, false);
        });

        return () => subscription.unsubscribe();
    }, [displayedFilters, filterValues, setFilters, trigger, watch]);

    return (
        <FormProvider {...form}>
            <FormGroupsProvider>
                <FilterFormBase filters={filters} {...rest} />
            </FormGroupsProvider>
        </FormProvider>
    );
};

SyncedFilterForm.propTypes = {
    defaultValues: PropTypes.object,
    filters: PropTypes.arrayOf(PropTypes.node),
    className: PropTypes.string,
};
